import { gsap } from "gsap";

export default class Intro {
    constructor() {
        this.root = document.querySelector(".js-intro");
        this.canvas = this.root?.querySelector(".js-intro-canvas");

        // ---- 形状・座標系 ----
        this.lineViewBox = { width: 2281.576, height: 116.681 };
        this.linePoints = [
            [0, 63.441],
            [1035.816, 64.125],
            [1096.111, 17.82],
            [1058.789, 98.011],
            [1156.271, 0],
            [1098.896, 116.681],
            [1208.632, 4.243],
            [1150.701, 112.437],
            [1226.012, 42.005],
            [1218.449, 64.125],
            [2281.576, 61.697],
        ];

        // ---- タイミング設定 ----
        this.timing = {
            startDelay: 0.5, // 開始ディレイ
            // 線を描く区間。end/offset を trim proxy で補間する区切り（元 trimKeyframes 相当）
            draw: [
                { at: 0.0, end: 0, offset: 122.49 }, // 0f
                { at: 0.8, end: 31.103, offset: 122.49, ease: "power2.out" }, // 48f 中央部分の描画完了
                { at: 0.87, end: 31.103, offset: 122.49 }, // 52f キープ
                { at: 2.4, end: 100, offset: 0, ease: "power2.out" }, // 102f 左右に広がって全体表示
            ],
            rotateStart: 48 / 60, // 回転・拡大の開始（draw 内の秒）
            splitStart: 1.55, // パネル分割の開始（線が描き切った後に切り替える）
            openDuration: 0.5, // パネルが開く時間
        };

        // ---- モーション量 ----
        this.currentRotation = 0; //回転角度の初期値
        this.currentLineScale = 1; //線の拡大率の初期値
        this.currentPanelMove = 0; //パネルの移動量の初期値

        this.finalRotation = -10; // 最終的に到達する回転角度
        this.finalScale = 8; // 最終的に到達する拡大率（開ききった時点）
        this.panelDiagonalMoveRatio = 0.7; // パネルの斜め移動比率（真上下だとギザギザ同士が被るため角度をつけて開く）
        this.maxPixelRatio = 2; // 描画解像度の上限。重い場合は1.5等に下げると負荷を直接減らせる
        this.isReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.init();
    }

    init() {
        if (!this.root || !this.canvas) return;

        this.ctx = this.canvas.getContext("2d");
        this.length = this.getLineLength();
        // 線のtrim状態（GSAPがこのオブジェクトを直接補間する。旧strokeDasharray相当）
        this.trim = { end: this.timing.draw[0].end, offset: this.timing.draw[0].offset };
        this.setLayout();
        this.root.classList.add("-is-ready");

        this.handleResize = () => this.setLayout();
        window.addEventListener("resize", this.handleResize);

        if (this.isReducedMotion) {
            this.finish();
            return;
        }

        const start = () => this.play();

        if (document.readyState === "complete") {
            start();
            return;
        }

        window.addEventListener("load", start, { once: true });
    }

    // ===== アニメーション本体 =====
    play() {
        this.root.classList.add("-is-active");

        const draw = this.timing.draw;
        // 各tweenは状態だけを補間し、描画はtickごとに1回だけ行う
        const tl = gsap.timeline({
            delay: this.timing.startDelay,
            onUpdate: () => this.render(),
        });
        this.timeline = tl;

        // --- 線のtrim（区間ごとに end/offset を補間） ---
        for (let i = 1; i < draw.length; i++) {
            const prev = draw[i - 1];
            const cur = draw[i];
            tl.to(
                this.trim,
                {
                    end: cur.end,
                    offset: cur.offset,
                    duration: cur.at - prev.at,
                    ease: cur.ease || "none",
                },
                prev.at // timeline 上の絶対位置（秒）
            );
        }

        // --- 回転・拡大（rotateStart から open終わりまで通しで 0→finalRotation / 1→finalScale） ---
        // 途中で止めず一本のtweenにすることで、分割時の段差をなくす
        const scaleEnd = this.timing.splitStart + this.timing.openDuration;
        tl.to(
            this,
            {
                currentRotation: this.finalRotation,
                currentLineScale: this.finalScale,
                duration: scaleEnd - this.timing.rotateStart,
                ease: "power2.out",
            },
            this.timing.rotateStart
        );

        // --- パネル分割の確定 → 開き ---
        tl.call(() => this.enterSplit(), null, this.timing.splitStart);

        const openState = { progress: 0 };
        tl.to(
            openState,
            {
                progress: 1,
                duration: this.timing.openDuration,
                ease: "power3.Out",
                onUpdate: () => {
                    this.currentPanelMove = this.viewHeight * openState.progress;
                },
            },
            this.timing.splitStart
        );

        tl.call(() => this.finish());
    }

    // 分割状態の確定（パネルポリゴンは setLayout で常に最新化済み）
    enterSplit() {
        this.isSplitting = true;
        this.currentPanelMove = 0;
    }

    // ===== 描画ロジック =====

    //リサイズ対応
    setLayout() {
        // window.innerWidth はスクロールバー込みの値で実描画幅とズレるため、要素の実測サイズを使う
        const rect = this.root.getBoundingClientRect();
        const width = (this.viewWidth = rect.width);
        const height = (this.viewHeight = rect.height);
        // iOS Safariでは箱(100lvh)がアドレスバー裏まで伸びるため、演出の中心は可視領域の中心に合わせる
        this.centerY = Math.min(height, window.innerHeight) / 2;
        this.pixelRatio = Math.min(window.devicePixelRatio || 1, this.maxPixelRatio);
        this.canvas.width = Math.round(width * this.pixelRatio);
        this.canvas.height = Math.round(height * this.pixelRatio);

        const targetLineWidth = this.getTargetLineWidth(width);
        this.scale = targetLineWidth / this.lineViewBox.width;
        const strokeWidth = width <= 780 ? 2.5 : 4;
        this.baseStrokeWidth = strokeWidth / this.scale;

        this.setPanelPaths(width, height);

        if (this.isSplitting) {
            this.currentPanelMove = height;
        }

        this.render();
    }

    //ブラウザ幅によって線の太さを変更
    getTargetLineWidth(width) {
        if (width <= 780) return width * 3;
        if (width <= 1399) return width * 2.3;

        return width;
    }

    //波形の折れ線の全長（旧getTotalLength相当）
    getLineLength() {
        let length = 0;

        for (let i = 1; i < this.linePoints.length; i++) {
            const [x1, y1] = this.linePoints[i - 1];
            const [x2, y2] = this.linePoints[i];
            length += Math.hypot(x2 - x1, y2 - y1);
        }

        return length;
    }

    //波形を元に上下のパネルのパスを設定
    setPanelPaths(width, height) {
        const points = this.linePoints.map(([x, y]) => {
            const scaledX = (x - this.lineViewBox.width / 2) * this.scale;
            const scaledY = (y - this.lineViewBox.height / 2) * this.scale;

            return {
                x: scaledX + width / 2,
                y: scaledY + this.centerY,
            };
        });
        const overscan = Math.max(width, height) * 0.3;
        const leftPoint = this.getExtendedPoint(points[0], points[1], -overscan);
        const rightPoint = this.getExtendedPoint(points[points.length - 1], points[points.length - 2], width + overscan);
        // 右端→左端へ波形をなぞる点列（パネルの波側の輪郭）
        const waveToLeft = [leftPoint, ...points, rightPoint].reverse();

        const buildPath = (edgeY) => {
            const path = new Path2D();
            path.moveTo(-overscan, edgeY);
            path.lineTo(width + overscan, edgeY);
            waveToLeft.forEach((point) => path.lineTo(point.x, point.y));
            path.lineTo(-overscan, edgeY);
            path.closePath();

            return path;
        };

        this.topPanelPath = buildPath(0);
        this.bottomPanelPath = buildPath(height);
    }

    //線が画面外に出るように、端点を延長した座標を返す
    getExtendedPoint(edgePoint, innerPoint, targetX) {
        const dx = edgePoint.x - innerPoint.x;
        const dy = edgePoint.y - innerPoint.y;
        const ratio = dx ? (targetX - edgePoint.x) / dx : 0;

        return {
            x: targetX,
            y: edgePoint.y + dy * ratio,
        };
    }

    //現在の状態を1フレームぶん描画
    render() {
        const ctx = this.ctx;
        const width = this.viewWidth;
        const height = this.viewHeight;

        ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = "#000";

        if (!this.isSplitting) {
            this.renderCover(ctx, width, height);
            return;
        }

        this.renderPanels(ctx, width, height);
    }

    //分割前: 黒カバーを塗り、線をくり抜く
    renderCover(ctx, width, height) {
        ctx.fillRect(0, 0, width, height);
        this.cutLine(ctx, width, height);
    }

    //線の形をdestination-outで「くり抜く」（旧SVG luminanceマスク相当）
    cutLine(ctx, width, height) {
        ctx.save();
        ctx.globalCompositeOperation = "destination-out";

        // 可視領域の中心基準で回転・拡大（旧 線のtransform属性と同じ順序）
        ctx.translate(width / 2, this.centerY);
        ctx.rotate((this.currentRotation * Math.PI) / 180);
        const scale = this.scale * this.currentLineScale;
        ctx.scale(scale, scale);
        ctx.translate(-this.lineViewBox.width / 2, -this.lineViewBox.height / 2);

        ctx.beginPath();
        this.linePoints.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));

        ctx.lineWidth = this.baseStrokeWidth;
        ctx.lineCap = "butt";
        ctx.lineJoin = "miter";
        ctx.miterLimit = 20;
        this.applyTrim(ctx);
        ctx.strokeStyle = "#000";
        ctx.stroke();
        ctx.restore();
    }

    //分割後: 上下パネルを斜め方向に平行移動で開く。回転・拡大は画面中心基準（旧panelGroupのtransform相当）
    //パネルはセンターラインで突き合わせた状態（スキマ0）から開き始め、線のくり抜きを重ねて適用し続けることで、
    //平行移動では細くなってしまう急斜面部分も含めスキマが常に線の形・太さを保つ
    renderPanels(ctx, width, height) {
        ctx.save();
        ctx.translate(width / 2, this.centerY);
        ctx.rotate((this.currentRotation * Math.PI) / 180);
        ctx.scale(this.currentLineScale, this.currentLineScale);
        ctx.translate(-width / 2, -this.centerY);

        const move = this.getPanelMove();
        const diagonalMove = move * this.panelDiagonalMoveRatio;

        ctx.save();
        ctx.translate(diagonalMove, -move);
        ctx.fill(this.topPanelPath);
        ctx.restore();

        ctx.translate(-diagonalMove, move);
        ctx.fill(this.bottomPanelPath);
        ctx.restore();

        // 分割の瞬間は直前のフレームと同一の見た目になり、開ききるとスキマの内側に隠れて自然に消える
        this.cutLine(ctx, width, height);
    }

    //パネルの片側移動量（グループ座標系）
    getPanelMove() {
        const panelScale = this.currentLineScale || 1;

        return this.currentPanelMove / panelScale;
    }

    // 線のtrimをdash設定に反映（旧strokeDasharray/strokeDashoffset相当）
    applyTrim(ctx) {
        const offsetPercent = ((this.trim.offset || 0) / 360) * 100;
        const startPercent = offsetPercent;
        const endPercent = this.trim.end + offsetPercent;
        const start = this.length * (startPercent / 100);
        const visible = this.length * ((endPercent - startPercent) / 100);

        if (this.trim.end >= 100) {
            ctx.setLineDash([]);
            ctx.lineDashOffset = 0;
            return;
        }

        ctx.setLineDash([Math.max(visible, 0), this.length]);
        ctx.lineDashOffset = -start;
    }

    // 最終状態に固定
    finish() {
        this.isSplitting = true;
        this.currentRotation = this.finalRotation;
        this.currentLineScale = this.finalScale;
        this.currentPanelMove = this.viewHeight;
        this.render();
        window.removeEventListener("resize", this.handleResize);

        requestAnimationFrame(() => {
            this.root.classList.add("-is-finished");
        });
    }
}
